import { ICD10CM_SYSTEM } from "@/lib/diagnosisApi";
import { reasonCodings } from "@/lib/patientChart";

export const DIAGNOSIS_SOURCES = {
  lookup: "lookup",
  common: "common",
  prior: "prior",
  problemList: "problem-list",
  encounter: "encounter",
};

export function diagnosisStorageKey(encounterId) {
  return `emrbymd.v3.encounter.diagnoses.${encounterId}`;
}

export function catalogItemToDiagnosis(item) {
  const code = item?.code || item?.codeNormalized || "";
  return {
    code,
    description: item?.description || item?.shortDescription || item?.display || "",
    system: item?.codeSystem || item?.system || ICD10CM_SYSTEM,
    isBillable: item?.isBillable !== false,
  };
}

export function toEncounterDiagnosis(item, extras = {}) {
  const base = catalogItemToDiagnosis(item);
  return {
    ...base,
    isPrimary: extras.isPrimary ?? item?.isPrimary === true,
    source: extras.source || item?.source || DIAGNOSIS_SOURCES.lookup,
  };
}

function compactDiagnosis(item) {
  const diagnosis = toEncounterDiagnosis(item);
  if (!diagnosis.code) return null;
  return diagnosis;
}

export function readLocalDiagnoses(encounterId) {
  const state = readEncounterDiagnosisState(encounterId);
  return state.items;
}

export function readEncounterDiagnosisState(encounterId) {
  if (!encounterId) return { initialized: false, items: [] };
  try {
    const raw = localStorage.getItem(diagnosisStorageKey(encounterId));
    if (!raw) return { initialized: false, items: [] };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return {
        initialized: true,
        legacyExtras: true,
        items: parsed.map((item) => compactDiagnosis(item)).filter(Boolean),
      };
    }
    const items = Array.isArray(parsed?.items)
      ? parsed.items.map((item) => compactDiagnosis(item)).filter(Boolean)
      : [];
    return { initialized: parsed?.initialized === true, items };
  } catch {
    return { initialized: false, items: [] };
  }
}

export function writeLocalDiagnoses(encounterId, items) {
  writeEncounterDiagnosisState(encounterId, { initialized: true, items });
}

export function writeEncounterDiagnosisState(encounterId, state) {
  if (!encounterId) return;
  localStorage.setItem(
    diagnosisStorageKey(encounterId),
    JSON.stringify({
      initialized: state?.initialized !== false,
      items: (state?.items || []).map((item) => compactDiagnosis(item)).filter(Boolean),
    }),
  );
}

export function encounterDiagnosesFromReasonCodes(encounter) {
  return reasonCodings(encounter).map((reason, index) =>
    toEncounterDiagnosis(
      {
        code: reason.code,
        description: reason.display,
        system: reason.system,
      },
      {
        isPrimary: index === 0,
        source: DIAGNOSIS_SOURCES.encounter,
      },
    ),
  );
}

export function loadEncounterDiagnoses(encounterId, encounter) {
  const stored = readEncounterDiagnosisState(encounterId);
  if (stored.initialized && stored.legacyExtras) {
    let items = encounterDiagnosesFromReasonCodes(encounter);
    for (const extra of stored.items) {
      items = addLocalDiagnosis(items, extra, extra.source || DIAGNOSIS_SOURCES.lookup).items;
    }
    writeEncounterDiagnosisState(encounterId, { initialized: true, items });
    return items;
  }
  if (stored.initialized) return stored.items;
  const seeded = encounterDiagnosesFromReasonCodes(encounter);
  writeEncounterDiagnosisState(encounterId, { initialized: true, items: seeded });
  return seeded;
}

export function toReasonCodeableConcept(item) {
  const diagnosis = catalogItemToDiagnosis(item);
  const coding = {
    system: diagnosis.system || ICD10CM_SYSTEM,
    code: diagnosis.code,
    display: diagnosis.description || undefined,
  };
  return {
    coding: [coding],
    text: diagnosis.description || diagnosis.code,
  };
}

export function applyEncounterDiagnoses(encounter, items = []) {
  if (!encounter) return encounter;
  return {
    ...encounter,
    reasonCode: (items || [])
      .map((item) => compactDiagnosis(item))
      .filter(Boolean)
      .map((item) => toReasonCodeableConcept(item)),
  };
}

export function mergeEncounterDiagnoses(encounter, extras = []) {
  if (!encounter) return encounter;
  const existing = encounter?.reasonCode || [];
  const seen = new Set(
    reasonCodings(encounter)
      .map((reason) => reason.code)
      .filter(Boolean),
  );
  const added = [];
  for (const item of extras) {
    const diagnosis = catalogItemToDiagnosis(item);
    if (!diagnosis.code || seen.has(diagnosis.code)) continue;
    seen.add(diagnosis.code);
    added.push(toReasonCodeableConcept(diagnosis));
  }
  if (!added.length) return encounter;
  return {
    ...encounter,
    reasonCode: [...existing, ...added],
  };
}

export function addLocalDiagnosis(items, item, source = DIAGNOSIS_SOURCES.lookup) {
  const diagnosis = toEncounterDiagnosis(item, { source, isPrimary: false });
  if (!diagnosis.code) return { items, added: false };
  if ((items || []).some((existing) => existing.code === diagnosis.code)) {
    return { items, added: false };
  }
  const next = [...(items || []), diagnosis];
  if (next.length === 1) next[0] = { ...next[0], isPrimary: true };
  return { items: next, added: true };
}

export function removeEncounterDiagnosis(items, code) {
  return (items || []).filter((item) => item.code !== code);
}

export function setPrimaryEncounterDiagnosis(items, code) {
  return (items || []).map((item) => ({
    ...item,
    isPrimary: item.code === code,
  }));
}

export function clearPrimaryEncounterDiagnosis(items) {
  return (items || []).map((item) => ({ ...item, isPrimary: false }));
}

export function moveEncounterDiagnosis(items, code, direction) {
  const list = [...(items || [])];
  const index = list.findIndex((item) => item.code === code);
  if (index < 0) return list;
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= list.length) return list;
  const [row] = list.splice(index, 1);
  list.splice(nextIndex, 0, row);
  return list;
}

export function priorDiagnosesFromEncounters(encounters, currentEncounterId) {
  const byCode = new Map();
  for (const enc of encounters || []) {
    if (currentEncounterId && enc?.id === currentEncounterId) continue;
    for (const reason of reasonCodings(enc)) {
      if (!reason.code || byCode.has(reason.code)) continue;
      byCode.set(reason.code, {
        code: reason.code,
        description: reason.display || "",
        system: reason.system || ICD10CM_SYSTEM,
      });
    }
  }
  return [...byCode.values()];
}
